/**
 * F5 — Color Picker / Palette extract (`client_pixel`).
 *
 * Pure-canvas median-cut quantization on a 256×256 downsample. Returns a
 * 5-7 color palette + a swatch-strip PNG suitable for the Result slot.
 */

import { registerClientHandler } from './registry.js'
import { loadImage } from '../utils/mockup-composite.js'

const SAMPLE_SIZE = 256

let _activeListeners = []
let _activeRoot = null

function track(target, event, fn) {
  target.addEventListener(event, fn)
  _activeListeners.push(() => target.removeEventListener(event, fn))
}

function cleanup() {
  while (_activeListeners.length) {
    try { _activeListeners.pop()() } catch {}
  }
  _activeRoot = null
}

export const clientPixelHandler = {
  mountControls(rootEl, ctx) {
    cleanup()
    _activeRoot = rootEl
    return mountPaletteUI(rootEl, ctx)
  },

  unmount() {
    cleanup()
    if (_activeRoot) _activeRoot.innerHTML = ''
  },

  async run(ctx) {
    const k = clampK(ctx.config?.colors)
    const palette = await extractPalette(ctx.image, k)
    return {
      result_data_url: paintSwatchStrip(palette),
      metadata: { palette },
    }
  },
}

registerClientHandler('clientPixelHandler', clientPixelHandler)

// ---- UI ----

function mountPaletteUI(rootEl, ctx) {
  const k = clampK(ctx.config?.colors)
  rootEl.innerHTML = `
    <div class="ctt-palette">
      <div class="ctt-controls">
        <label>Colors</label>
        <input type="range" id="paletteK" min="3" max="9" value="${k}" />
        <span class="ctt-value" data-bind="k">${k}</span>
        <button type="button" class="ctt-pill" id="paletteCopy">Copy hexes</button>
      </div>
      <div class="ctt-palette-strip" id="paletteStrip"></div>
    </div>
  `

  const slider = rootEl.querySelector('#paletteK')
  const label = rootEl.querySelector('[data-bind="k"]')
  const strip = rootEl.querySelector('#paletteStrip')
  const copyBtn = rootEl.querySelector('#paletteCopy')

  let currentPalette = []

  const recompute = async () => {
    const kk = clampK(Number(slider.value))
    if (label) label.textContent = String(kk)
    try {
      currentPalette = await extractPalette(ctx.image, kk)
      renderStrip(strip, currentPalette)
      ctx.onResult?.({
        result_data_url: paintSwatchStrip(currentPalette),
        metadata: { palette: currentPalette },
      })
    } catch (err) {
      ctx.onError?.(err)
    }
  }

  let timer = null
  track(slider, 'input', () => {
    clearTimeout(timer)
    timer = setTimeout(recompute, 100)
  })
  track(copyBtn, 'click', async () => {
    const text = currentPalette.map(p => p.hex).join(', ')
    try {
      await navigator.clipboard?.writeText(text)
    } catch {}
  })

  recompute()
}

function renderStrip(stripEl, palette) {
  stripEl.innerHTML = palette.map(p => `
    <div class="palette-swatch" style="background:${p.hex}" title="${p.hex} (${Math.round(p.weight * 100)}%)">
      <span class="palette-hex">${p.hex}</span>
    </div>
  `).join('')
}

// ---- Algorithm ----

export async function extractPalette(imageUrl, k = 7) {
  const img = await loadImage(imageUrl)
  const canvas = document.createElement('canvas')
  canvas.width = canvas.height = SAMPLE_SIZE
  const c = canvas.getContext('2d')
  c.drawImage(img, 0, 0, SAMPLE_SIZE, SAMPLE_SIZE)
  const { data } = c.getImageData(0, 0, SAMPLE_SIZE, SAMPLE_SIZE)

  const pixels = []
  for (let i = 0; i < data.length; i += 4) {
    if (data[i + 3] < 128) continue
    pixels.push([data[i], data[i + 1], data[i + 2]])
  }
  if (!pixels.length) return []

  const buckets = medianCut(pixels, k)
  const total = pixels.length
  const palette = buckets.map(b => ({
    hex: rgbToHex(b.center),
    weight: b.size / total,
  })).sort((a, b) => b.weight - a.weight)

  return palette
}

function medianCut(pixels, k) {
  const initial = makeBucket(pixels)
  const buckets = [initial]
  while (buckets.length < k) {
    let target = -1
    let maxRange = -1
    for (let i = 0; i < buckets.length; i++) {
      const r = bucketRange(buckets[i])
      if (r > maxRange) { maxRange = r; target = i }
    }
    if (target < 0 || maxRange === 0) break
    const [a, b] = splitBucket(buckets[target])
    buckets.splice(target, 1, a, b)
  }
  return buckets
}

function makeBucket(px) {
  if (!px.length) return { pixels: [], size: 0, center: [0, 0, 0] }
  let r = 0, g = 0, b = 0
  for (const p of px) { r += p[0]; g += p[1]; b += p[2] }
  const n = px.length
  return {
    pixels: px,
    size: n,
    center: [Math.round(r / n), Math.round(g / n), Math.round(b / n)],
  }
}

function bucketRange(bucket) {
  const px = bucket.pixels
  if (!px.length) return 0
  let rMin = 255, rMax = 0, gMin = 255, gMax = 0, bMin = 255, bMax = 0
  for (const p of px) {
    if (p[0] < rMin) rMin = p[0]; if (p[0] > rMax) rMax = p[0]
    if (p[1] < gMin) gMin = p[1]; if (p[1] > gMax) gMax = p[1]
    if (p[2] < bMin) bMin = p[2]; if (p[2] > bMax) bMax = p[2]
  }
  return Math.max(rMax - rMin, gMax - gMin, bMax - bMin)
}

function splitBucket(bucket) {
  const px = bucket.pixels
  let rMin = 255, rMax = 0, gMin = 255, gMax = 0, bMin = 255, bMax = 0
  for (const p of px) {
    if (p[0] < rMin) rMin = p[0]; if (p[0] > rMax) rMax = p[0]
    if (p[1] < gMin) gMin = p[1]; if (p[1] > gMax) gMax = p[1]
    if (p[2] < bMin) bMin = p[2]; if (p[2] > bMax) bMax = p[2]
  }
  const ranges = [rMax - rMin, gMax - gMin, bMax - bMin]
  const channel = ranges.indexOf(Math.max(...ranges))
  const sorted = [...px].sort((a, b) => a[channel] - b[channel])
  const mid = sorted.length >> 1
  return [makeBucket(sorted.slice(0, mid)), makeBucket(sorted.slice(mid))]
}

function rgbToHex([r, g, b]) {
  return '#' + [r, g, b].map(v => v.toString(16).padStart(2, '0')).join('')
}

function clampK(v) {
  const n = Number(v)
  if (!Number.isFinite(n)) return 7
  return Math.max(3, Math.min(9, Math.round(n)))
}

// ---- Swatch image ----

// Image-content colors, not themed UI chrome — the swatch strip below is
// rendered into an exported PNG (empty-palette fill + per-swatch label color),
// so the exported pixels must not follow the theme.
function paintSwatchStrip(palette, width = 1024, height = 256) {
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const c = canvas.getContext('2d')
  if (!palette.length) {
    c.fillStyle = '#222'
    c.fillRect(0, 0, width, height)
    return canvas.toDataURL('image/png')
  }
  let x = 0
  let totalWeight = palette.reduce((acc, p) => acc + p.weight, 0) || 1
  for (const p of palette) {
    const w = (p.weight / totalWeight) * width
    c.fillStyle = p.hex
    c.fillRect(x, 0, Math.ceil(w), height)
    c.fillStyle = readableTextColor(p.hex)
    c.font = 'bold 18px system-ui, sans-serif'
    c.textAlign = 'center'
    c.textBaseline = 'middle'
    c.fillText(p.hex, x + w / 2, height / 2)
    x += w
  }
  return canvas.toDataURL('image/png')
}

function readableTextColor(hex) {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  const lum = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255
  return lum > 0.5 ? '#111' : '#fff'
}
